import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'

// components
import { Text, Format, SquareIcon } from '../../../../..'

// selectors
import {
  selectAnnualAppreciationPercent,
  selectAppreciationGrowthStatus,
} from '../../../../../../store/selectors/shouldYouSell'

// constants
import {
  SHOULD_YOU_SELL_HIGH_GROWTH_STATUS,
  SHOULD_YOU_SELL_MODERATE_GROWTH_STATUS,
  SHOULD_YOU_SELL_SLOW_GROWTH_STATUS,
} from '../../../../../../constants/shouldYouSell'

// styles
import baseStyles from '../../ShouldYouSell.module.scss'
import styles from '../Appreciation.module.scss'

const AppreciationLabel = () => {
  const appreciationAmount = useSelector(selectAnnualAppreciationPercent)
  const growthStatus = useSelector(selectAppreciationGrowthStatus)

  const headingTitles = status => {
    switch (status) {
      case SHOULD_YOU_SELL_HIGH_GROWTH_STATUS:
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.title.high'
            defaultMessage='High appreciation'
          />
        )
      case SHOULD_YOU_SELL_MODERATE_GROWTH_STATUS:
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.title.moderate'
            defaultMessage='Moderate appreciation'
          />
        )
      case SHOULD_YOU_SELL_SLOW_GROWTH_STATUS:
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.title.slow'
            defaultMessage='Slow home value growth'
          />
        )
      default:
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.title.depreciation'
            defaultMessage='Uncertain appreciation'
          />
        )
    }
  }

  const headingSubTitles = status => {
    switch (status) {
      case SHOULD_YOU_SELL_HIGH_GROWTH_STATUS:
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.subTitle.high'
            defaultMessage='Your home value grew {appreciationPercent}'
            values={{
              appreciationPercent: (
                <span className={styles.bold}>
                  <Format.Percentage
                    value={appreciationAmount}
                    format='0.0'
                    roundLessThanOne={false}
                  />
                  <FormattedMessage
                    id='ShouldYouSell.appreciation.year.label'
                    defaultMessage=' / year'
                  />
                </span>
              ),
            }}
          />
        )
      case SHOULD_YOU_SELL_MODERATE_GROWTH_STATUS:
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.subTitle.moderate'
            defaultMessage='Your home value grew {appreciationPercent}'
            values={{
              appreciationPercent: (
                <span className={styles.bold}>
                  <Format.Percentage
                    value={appreciationAmount}
                    format='0.0'
                    roundLessThanOne={false}
                  />
                  <FormattedMessage
                    id='ShouldYouSell.appreciation.year.label'
                    defaultMessage=' / year'
                  />
                </span>
              ),
            }}
          />
        )
      case SHOULD_YOU_SELL_SLOW_GROWTH_STATUS:
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.subtitle.slow'
            defaultMessage='Your home grew only {appreciationPercent}'
            values={{
              appreciationPercent: (
                <span className={styles.bold}>
                  <Format.Percentage
                    value={appreciationAmount}
                    format='0.0'
                    roundLessThanOne={false}
                  />
                  <FormattedMessage
                    id='ShouldYouSell.appreciation.year.label'
                    defaultMessage=' / year'
                  />
                </span>
              ),
            }}
          />
        )
      default:
        if (appreciationAmount >= 0) {
          return (
            <FormattedMessage
              id='ShouldYouSell.appreciation.subtitle.depreciation.positive'
              defaultMessage='Your home value only grew {appreciationPercent}'
              values={{
                appreciationPercent: (
                  <span className={styles.bold}>
                    <Format.Percentage
                      value={appreciationAmount}
                      format='0.0'
                      roundLessThanOne={false}
                    />
                  </span>
                ),
              }}
            />
          )
        }
        return (
          <FormattedMessage
            id='ShouldYouSell.appreciation.subtitle.depreciation.negative'
            defaultMessage='Your home value dropped {appreciationPercent}'
            values={{
              appreciationPercent: (
                <span className={styles.bold}>
                  <Format.Percentage
                    value={appreciationAmount}
                    format='0.0'
                    roundLessThanOne={false}
                  />
                </span>
              ),
            }}
          />
        )
    }
  }

  const determineColor = status => {
    switch (status) {
      case SHOULD_YOU_SELL_HIGH_GROWTH_STATUS:
      case SHOULD_YOU_SELL_MODERATE_GROWTH_STATUS:
        return 'success'
      default:
        return 'warning'
    }
  }

  const titles = useMemo(() => headingTitles(growthStatus), [growthStatus])
  const subTitles = useMemo(
    () => headingSubTitles(growthStatus),
    [growthStatus]
  )
  const textColor = useMemo(() => determineColor(growthStatus), [growthStatus])
  const iconColor = useMemo(() => determineColor(growthStatus), [growthStatus])

  return (
    <>
      <SquareIcon
        className={baseStyles.icon}
        backgroundColor={iconColor}
        icon='home'
      />
      <div className={baseStyles.label}>
        <Text size='s' variant={textColor} weight='semibold'>
          {titles}
        </Text>
        <Text size='xs' variant='neutralCool' variantCount='600'>
          {subTitles}
        </Text>
      </div>
    </>
  )
}

export default AppreciationLabel
